import * as _ from 'lodash';
import * as qs from 'qs';
import type { SetOptional } from 'type-fest';

import { nuc } from '../../../../utils';
import { icons } from '../../../generic';
import type {
  DependencyInfoOption,
  FixPlanReportOptionDetail,
  FixPlanReportOptions,
  ProjectContext,
  ProjectGroupContext,
  ReportAction,
  ReportCategory,
  ReportContext,
  ReportExclusionOptionDetail,
  ReportExclusionOptions,
  ReportFormatDetail,
  ReportFormatType,
  ReportOptionDetail,
  ReportOptions,
  ReportURLParams,
} from '../shared/types';

/**
 * Constants
 */

export const ProjectType = {
  Project: 'project',
  ReleaseGroup: 'releaseGroup',
} as const;

// Categories
export const ReportCategories = {
  Licensing: 'licensing',
  Vulnerability: 'vulnerability',
  SBOM: 'sbom',
  RemediationGuidance: 'remediation-guidance',
} as const;

export const ReportCategoryTitles: Record<ReportCategory, string> = {
  licensing: 'Licensing',
  vulnerability: 'Vulnerability',
  sbom: 'SBOM',
  'remediation-guidance': 'Remediation Guidance',
} as const;

// Formats
export const ReportFormats = {
  // Licensing
  HTML: 'HTML',
  MD: 'MD',
  CSV: 'CSV',
  TXT: 'TXT',
  // Vulnerability + Licensing
  PDF: 'PDF',
  // SBOM
  SPDX: 'SPDX',
  SPDX_JSON: 'SPDX_JSON',
  CYCLONEDX_JSON: 'CYCLONEDX_JSON',
  CYCLONEDX_XML: 'CYCLONEDX_XML',
  // Fix Plans
  JSON: 'JSON',
} as const;

export const ReportFormatDetails: Record<ReportFormatType, ReportFormatDetail> = {
  [ReportFormats.HTML]: {
    key: ReportFormats.HTML,
    name: 'HTML',
    icon: icons.html5,
    type: [ReportCategories.Licensing],
    supportsReleaseGroups: true,
  },
  [ReportFormats.MD]: {
    key: ReportFormats.MD,
    name: 'MARKDOWN',
    icon: icons.fileWordO,
    type: [ReportCategories.Licensing],
    supportsReleaseGroups: true,
  },
  [ReportFormats.PDF]: {
    key: ReportFormats.PDF,
    name: 'PDF',
    icon: icons.filePdfO,
    type: [ReportCategories.Licensing, ReportCategories.Vulnerability],
    supportsReleaseGroups: true,
  },
  [ReportFormats.CSV]: {
    key: ReportFormats.CSV,
    name: 'CSV',
    icon: icons.fileExcelO,
    type: [ReportCategories.Licensing],
    supportsReleaseGroups: true,
  },
  [ReportFormats.TXT]: {
    key: ReportFormats.TXT,
    name: 'PLAIN TEXT',
    icon: icons.fileO,
    type: [ReportCategories.Licensing],
    supportsReleaseGroups: true,
  },
  [ReportFormats.SPDX]: {
    key: ReportFormats.SPDX,
    name: 'SPDX',
    icon: icons.fileTextO,
    type: [ReportCategories.SBOM],
    supportsReleaseGroups: true,
  },
  [ReportFormats.SPDX_JSON]: {
    key: ReportFormats.SPDX_JSON,
    name: 'SPDX (JSON)',
    icon: icons.fileTextO,
    type: [ReportCategories.SBOM],
    supportsReleaseGroups: true,
  },
  [ReportFormats.CYCLONEDX_JSON]: {
    key: ReportFormats.CYCLONEDX_JSON,
    name: 'CYCLONEDX (JSON)',
    icon: icons.fileTextO,
    type: [ReportCategories.SBOM],
    supportsReleaseGroups: true,
  },
  [ReportFormats.CYCLONEDX_XML]: {
    key: ReportFormats.CYCLONEDX_XML,
    name: 'CYCLONEDX (XML)',
    icon: icons.fileTextO,
    type: [ReportCategories.SBOM],
    supportsReleaseGroups: true,
  },
  [ReportFormats.JSON]: {
    key: ReportFormats.JSON,
    name: 'JSON',
    icon: icons.fileTextO,
    type: [ReportCategories.RemediationGuidance],
    supportsReleaseGroups: true,
  },
};

// Actions
export const ReportActions = {
  Preview: 'preview',
  Download: 'download',
  Email: 'email',
  Public: 'public',
  Publish: 'publish',
};

export const ReportOptionDetails: SetOptional<Record<keyof ReportOptions, ReportOptionDetail>, 'includePackageLabels'> = {
  includeConcludedLicenses: {
    key: 'includeConcludedLicenses',
    title: 'Concluded Licenses',
    description: `Licenses you've declared as governing the project`,
    category: [ReportCategories.Licensing],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeProjectLicense: {
    key: 'includeProjectLicense',
    title: 'Project Declared License',
    description: 'The license of the project analyzed by FOSSA',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeLicenseScan: {
    key: 'includeLicenseScan',
    title: 'First Party Licenses',
    description: 'The licenses of the project analyzed by FOSSA',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeDependencySummary: {
    key: 'includeDependencySummary',
    title: 'Dependency Summary',
    description: 'A table overview of each dependency, version, and license included in the analyzed project',
    category: [ReportCategories.Licensing],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeDirectDependencies: {
    key: 'includeDirectDependencies',
    title: 'Direct Dependencies',
    description: 'Open source packages explicitly included in the analyzed project (e.g. via pom.xml, package.json)',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeDeepDependencies: {
    key: 'includeDeepDependencies',
    title: 'Transitive Dependencies',
    description: 'Open source packages included via direct dependencies. Also referred to as deep dependencies.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeSnippetDependencies: {
    key: 'includeSnippetDependencies',
    title: 'Snippet Dependencies',
    description: 'Open source packages found by snippet scanning.',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includeLicenseList: {
    key: 'includeLicenseList',
    title: 'Full License List',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeFileMatches: {
    key: 'includeFileMatches',
    title: 'License File Matches',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: [ReportFormats.CSV, ReportFormats.TXT, ReportFormats.SPDX, ReportFormats.SPDX_JSON],
  },
  includeOpenVulnerabilities: {
    key: 'includeOpenVulnerabilities',
    title: 'Open Vulnerabilities',
    category: [ReportCategories.SBOM],
    format: [ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML],
  },
  includeClosedVulnerabilities: {
    key: 'includeClosedVulnerabilities',
    title: 'Closed Vulnerabilities',
    category: [ReportCategories.SBOM],
    format: [ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML],
  },
  includeSupportStatus: {
    key: 'includeSupportStatus',
    title: 'Support Status',
    category: [ReportCategories.SBOM],
    format: [ReportFormats.CYCLONEDX_JSON, ReportFormats.CYCLONEDX_XML],
  },
  includeLicenseHeaders: {
    key: 'includeLicenseHeaders',
    title: 'License Headers',
    category: [ReportCategories.Licensing],
    format: [ReportFormats.TXT],
  },
  includeCopyrightList: {
    key: 'includeCopyrightList',
    title: 'Copyrights',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeNonLicenseCopyrights: {
    key: 'includeNonLicenseCopyrights',
    title: 'Copyrights from non-license text',
    description:
      'Include copyrights from all files. Without this flag, we only show copyrights from files with licenses in them',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeNoticeFiles: {
    key: 'includeNoticeFiles',
    title: 'Notice Files',
    description: 'The NOTICE files of the project analyzed by FOSSA',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: _.without(Object.values(ReportFormats), ReportFormats.CSV),
  },
  includeHashAndVersionData: {
    key: 'includeHashAndVersionData',
    title: 'Hash and Version Data',
    description: 'Include hash and version data for git and go dependencies',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
  includePackageLabels: {
    key: 'includePackageLabels',
    title: 'Package Labels',
    description: 'Labels associated with a project\'s dependencies',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
  },
};

/**
 * This is a list of exclusion filters that are available for the report.
 * It is used to determine which filters to show in the UI and to build the URL params for the report.
 *
 * As more exclusion filters are added, this list as well as the
 * app/components/projects/generic/ReportGenerationV2/shared/components/Filter/exclusions/ExclusionGroup
 * component will need to be updated to support them.
 */
export const ReportOptionExclusionDetails: Record<keyof ReportExclusionOptions, ReportExclusionOptionDetail> = {
  packageLabels: {
    key: 'packageLabels',
    title: 'Package Labels',
    description: 'Labels associated with a project\'s dependencies',
    category: [ReportCategories.Licensing, ReportCategories.SBOM],
    format: Object.values(ReportFormats),
    scope: 'project',
  },
};

export const FixPlanReportOptionDetails: Record<keyof FixPlanReportOptions, FixPlanReportOptionDetail> = {
  includeQuickWins: {
    key: 'includeQuickWins',
    title: 'Quick Wins',
    alternativeTitle: 'Fix Quickly',
    description: 'Recommended vulnerability fixes that require only a patch upgrade',
  },
  includeHighPriority: {
    key: 'includeHighPriority',
    title: 'High Priority Fixes',
    alternativeTitle: 'Prioritize Now',
    description: 'Recommended fixes for high priority vulnerabilities',
  },
  includeLowPriority: {
    key: 'includeLowPriority',
    title: 'Low Priority Fixes',
    alternativeTitle: 'Prioritize Next',
    description: 'Recommended vulnerability fixes that are likely lower priority',
  },
  includeOutdatedDependencies: {
    key: 'includeOutdatedDependencies',
    title: 'Outdated Dependencies',
    alternativeTitle: 'Address Proactively',
    description: 'Dependencies that have fallen behind',
  },
  includeMalware: {
    key: 'includeMalware',
    title: 'Malicious Dependencies',
    alternativeTitle: 'Malicious Dependencies',
    description: 'Dependencies identified as malware that pose security risks',
  },
  deduplicateOutdatedDeps: {
    key: 'deduplicateOutdatedDeps',
    title: 'Exclude Vulnerable',
    alternativeTitle: 'Exclude Vulnerable',
    description: 'Remove outdated dependencies that have any vulnerability issues.',
    isNested: true,
  },
  includeTransitiveVulns: {
    key: 'includeTransitiveVulns',
    title: 'Transitive Vulnerabilities',
    alternativeTitle: 'Transitive Vulnerabilities',
    description:
      'Include transitive vulnerabilities that might be affected by the direct dependency fix recommendation.',
  },
};

export const DefaultDependencyInfoOptions: DependencyInfoOption[] = [
  'Library',
  'License',
  'CustomTextLicense',
  'OtherLicenses',
];

export const FixPlanInfoOptions = {
  HighPriority: 'High Priority',
  LowPriority: 'Low Priority',
  QuickWins: 'Quick Wins',
  OutdatedDeps: 'Outdated Dependencies',
} as const;

/**
 * URL Generation
 */
export const projectReportBaseURL = (revision: string) => `/api/revisions/${nuc(revision)}/attribution`;
export const groupReportBaseURL = (groupId: number, releaseId: number) =>
  `/api/project_group/${groupId}/release/${releaseId}/attribution`;

export const buildURLParams = (options: ReportURLParams) => qs.stringify(options);

export const getReportURL = (action: ReportAction, context: ReportContext, options: ReportURLParams) => {
  const { projectType, category } = context;

  // Translate actions to parameters and add options
  const reportOptions = {
    ...(action === ReportActions.Preview ? { preview: true } : {}),
    ...(action === ReportActions.Download ? { download: true } : {}),
    ...(action === ReportActions.Publish ? { isPublishing: true } : {}),
    ...options,
  };

  // Get Base URL depending on project type
  let baseUrl;
  if (projectType === ProjectType.Project) {
    const { revision } = context as ProjectContext;

    baseUrl = projectReportBaseURL(revision);
  } else {
    const { groupId, releaseId } = context as ProjectGroupContext;

    baseUrl = groupReportBaseURL(groupId, releaseId);
  }

  const urlParams = buildURLParams(reportOptions);

  // Security URL has a different format from licensing
  if (category === ReportCategories.Vulnerability) {
    return `${baseUrl}/security?${urlParams}`;
  }

  // Release group URL is slightly different from licensing
  if (projectType === ProjectType.ReleaseGroup) {
    return `${baseUrl}/${options.format}?${urlParams}`;
  }

  return `${baseUrl}/${action}?${urlParams}`;
};
